import React, { useRef, useState } from 'react';
import api from './api';
import { userDisplayName, toValidDate, taskCode, avancementTache, isOverdue, formatDate, isoToDisplayDate, displayDateToIso } from './helpers';

function isoLocal(d) {
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return z.toISOString().slice(0, 10);
}

/**
 * Éditeur en place d'un jalon, ouvert depuis le Gantt : nom et date, rien
 * d'autre — pour le reste (description, rattachements), l'onglet Jalons.
 */
function JalonEditeur({ jalon, onClose, onSaved }) {
  const [nom, setNom] = useState(jalon.name);
  const [date, setDate] = useState(isoToDisplayDate(jalon.due_date));
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);

  const enregistrer = (e) => {
    e.preventDefault();
    const n = nom.trim();
    if (!n) return setErreur('Le jalon doit garder un nom.');
    const iso = displayDateToIso(date);
    if (!date || iso === null) return setErreur('Date au format jj/mm/aaaa.');
    setEnvoi(true);
    api
      .put(`/tasks/${jalon.id}`, { name: n, due_date: iso, start_date: iso })
      .then(() => { onSaved(); onClose(); })
      .catch(() => setErreur('La modification a échoué.'))
      .finally(() => setEnvoi(false));
  };

  return (
    <form className="gantt-jalon-editeur" onSubmit={enregistrer} onClick={(e) => e.stopPropagation()} draggable={false} onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); }}>
      <div className="form-group">
        <label>Nom du jalon</label>
        <input value={nom} onChange={(e) => setNom(e.target.value)} autoFocus />
      </div>
      <div className="form-group">
        <label>Date visée</label>
        <input value={date} onChange={(e) => setDate(e.target.value)} placeholder="jj/mm/aaaa" />
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <button type="button" className="btn btn-sm" onClick={onClose}>Annuler</button>
        <button type="submit" className="btn btn-primary btn-sm" disabled={envoi}>{envoi ? '…' : 'Enregistrer'}</button>
      </div>
      {erreur && <div className="error-message">{erreur}</div>}
    </form>
  );
}

const MONTH_LABELS = ['JAN', 'FÉV', 'MAR', 'AVR', 'MAI', 'JUIN', 'JUIL', 'AOÛT', 'SEPT', 'OCT', 'NOV', 'DÉC'];

const BAR_COLOR = {
  Todo: '#8CA0B3',
  In_Progress: '#517997',
  In_Review: '#C19E54',
  Done: '#4CAF50',
  Blocked: '#D9534F',
};

// Au-delà de cette marge (en jours) autour de la période des tâches, on n'étire
// plus l'échelle pour inclure "aujourd'hui" — sinon un vieux jeu de données
// écraserait tout le graphique sur une échelle de plusieurs années.
const TODAY_MARGIN_DAYS = 120;

const CLE_HORS_JALON = 'hors-jalon';

/**
 * Couleur d'une barre de regroupement. L'ordre compte : un blocage l'emporte
 * sur un retard, un retard l'emporte sur l'avancement normal. On veut voir le
 * pire état du groupe, pas sa moyenne.
 */
function couleurGroupe(taches) {
  if (taches.length === 0) return BAR_COLOR.Todo;
  if (taches.some((t) => t.status === 'Blocked')) return BAR_COLOR.Blocked;
  if (taches.every((t) => t.status === 'Done')) return BAR_COLOR.Done;
  if (taches.some(isOverdue)) return BAR_COLOR.In_Review;
  return BAR_COLOR.In_Progress;
}

/**
 * Diagramme de Gantt.
 *
 * Deux problèmes se posent dès qu'un projet dépasse une vingtaine de tâches :
 * le graphique ne tient plus sur un écran, et il répète la même information
 * (dix approvisionnements aux mêmes dates font dix barres identiques). D'où le
 * repli par jalon : une ligne de synthèse par jalon, qu'on déplie d'un clic
 * pour voir le détail. Un projet de quarante tâches tient alors en quatre
 * lignes.
 *
 * `compact` sert au rapport de projet : la colonne de gauche n'affiche alors
 * que la référence de la tâche (TSK-0001) au lieu de son titre complet et de
 * son responsable, et les groupes restent repliés — le planning tient ainsi
 * sur une page imprimée.
 */
function Gantt({ tasks, users, onOpenTask, onReorderJalons, onJalonChanged, compact = false }) {
  // Jalon en cours d'édition (formulaire en place) et jalon en cours de
  // déplacement à la souris sur l'axe des dates.
  const [jalonEdite, setJalonEdite] = useState(null);
  const [glisseDate, setGlisseDate] = useState(null); // { id, iso, left }
  const [infoDeplacement, setInfoDeplacement] = useState('');

  const jalons = tasks.filter((t) => t.est_jalon);
  const groupable = jalons.length > 0;

  // Tout est replié au départ : c'est l'intérêt du regroupement. Sans jalon,
  // il n'y a rien à replier et on affiche la liste à plat comme avant.
  const [deplies, setDeplies] = useState(() => new Set());

  // Glisser-déposer des jalons. `glisse` est la ligne prise en main, `cible`
  // celle qu'on survole. `aGlisse` sert à ne pas replier un groupe juste parce
  // qu'un déposé a produit un clic dans la foulée.
  // La ligne saisie est tenue dans une ref *et* dans l'état : la ref sert à la
  // logique (elle est à jour dès l'évènement suivant), l'état sert à l'affichage
  // (il déclenche le rendu). S'appuyer sur le seul état ferait rater les
  // dépôts très rapides, la mise à jour n'étant pas encore appliquée.
  const glisseRef = useRef(null);
  const [glisse, setGlisse] = useState(null);
  const [cible, setCible] = useState(null);
  const aGlisse = useRef(false);

  const prendre = (id) => { glisseRef.current = id; setGlisse(id); };
  const relacher = () => { glisseRef.current = null; setGlisse(null); setCible(null); };

  const largeurLibelles = compact ? 78 : 220;

  if (tasks.length === 0) {
    return <div className="empty-state">Aucune tâche à afficher.</div>;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // --- Constitution des groupes ---------------------------------------------
  // Un groupe par jalon, dans l'ordre des dates, puis les tâches qui ne sont
  // rattachées à aucun jalon. Une tâche rattachée à un jalon supprimé retombe
  // dans « Hors jalon » plutôt que de disparaître.
  const idsJalons = new Set(jalons.map((j) => j.id));
  const travail = tasks.filter((t) => !t.est_jalon);

  const groupes = [];
  if (groupable) {
    // Même règle de tri qu'au serveur : l'ordre choisi à la main d'abord, la
    // date pour tout ce qui n'a jamais été déplacé.
    const rang = (j) => (Number.isFinite(Number(j.ordre)) && Number(j.ordre) > 0 ? Number(j.ordre) : 9999);
    const tries = [...jalons].sort(
      (a, b) =>
        rang(a) - rang(b) ||
        String(a.due_date || '9999').localeCompare(String(b.due_date || '9999'))
    );
    for (const jalon of tries) {
      groupes.push({
        cle: `jalon-${jalon.id}`,
        jalon,
        taches: travail.filter((t) => parseInt(t.jalon_id || 0) === jalon.id),
      });
    }
    const orphelines = travail.filter(
      (t) => !t.jalon_id || !idsJalons.has(parseInt(t.jalon_id))
    );
    if (orphelines.length > 0) {
      groupes.push({ cle: CLE_HORS_JALON, jalon: null, taches: orphelines });
    }
  } else {
    groupes.push({ cle: CLE_HORS_JALON, jalon: null, taches: travail, plat: true });
  }

  // On ignore les dates absentes ou invalides : une seule valeur corrompue
  // suffirait sinon à rendre toute l'échelle du graphique inutilisable.
  const dates = tasks.flatMap((t) => [toValidDate(t.start_date), toValidDate(t.due_date)]).filter(Boolean);

  if (dates.length === 0) {
    return <div className="empty-state">Aucune date valide à afficher sur le planning.</div>;
  }

  const minDate = new Date(Math.min(...dates));
  const maxDate = new Date(Math.max(...dates));

  const daysFromRange = today < minDate
    ? (minDate - today) / 86400000
    : today > maxDate
    ? (today - maxDate) / 86400000
    : 0;
  const includeToday = daysFromRange <= TODAY_MARGIN_DAYS;

  if (includeToday) {
    if (today < minDate) minDate.setTime(today.getTime());
    if (today > maxDate) maxDate.setTime(today.getTime());
  }

  const rangeStart = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
  const rangeEnd = new Date(maxDate.getFullYear(), maxDate.getMonth() + 1, 0);
  const totalDays = Math.max(1, (rangeEnd - rangeStart) / 86400000 + 1);

  const dayOffset = (d) => Math.max(0, (new Date(d) - rangeStart) / 86400000);
  const pct = (days) => (days / totalDays) * 100;

  // Months proportional to their real day count
  const months = [];
  let cursor = new Date(rangeStart);
  while (cursor <= rangeEnd) {
    const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    const clippedEnd = monthEnd > rangeEnd ? rangeEnd : monthEnd;
    const days = (clippedEnd - monthStart) / 86400000 + 1;
    months.push({ label: MONTH_LABELS[cursor.getMonth()], year: cursor.getFullYear(), left: pct(dayOffset(monthStart)), width: pct(days) });
    cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
  }

  // Weekly tick marks (every Monday) for a finer date trace
  const weekTicks = [];
  let wc = new Date(rangeStart);
  while (wc.getDay() !== 1) wc.setDate(wc.getDate() + 1);
  while (wc <= rangeEnd) {
    weekTicks.push({ left: pct(dayOffset(wc)), label: `${wc.getDate()}` });
    wc = new Date(wc.getFullYear(), wc.getMonth(), wc.getDate() + 7);
  }

  const todayInRange = includeToday && today >= rangeStart && today <= rangeEnd;
  const todayLeft = pct(dayOffset(today));

  const basculer = (cle) =>
    setDeplies((s) => {
      const suivant = new Set(s);
      if (suivant.has(cle)) suivant.delete(cle);
      else suivant.add(cle);
      return suivant;
    });

  const toutDeplie = groupes.every((g) => deplies.has(g.cle));

  // Les groupes porteurs d'un jalon, dans l'ordre affiché : c'est sur cette
  // liste que les flèches travaillent (« Hors jalon » reste toujours en bas).
  const groupesJalons = groupes.filter((g) => g.jalon);

  const deposerJalon = (surId) => {
    const ids = groupesJalons.map((g) => g.jalon.id);
    const depuis = ids.indexOf(glisseRef.current);
    const vers = ids.indexOf(surId);
    relacher();
    if (depuis === -1 || vers === -1 || depuis === vers) return;
    const suivant = [...ids];
    suivant.splice(vers, 0, suivant.splice(depuis, 1)[0]);
    onReorderJalons(suivant);
  };

  // --- Rendu d'une ligne de tâche -------------------------------------------
  const ligneTache = (task, indente) => {
    const startDate = toValidDate(task.start_date) || toValidDate(task.due_date);
    const endDate = toValidDate(task.due_date) || toValidDate(task.start_date);

    return (
      <div
        key={task.id}
        className={`gantt-row${indente ? ' gantt-row-fille' : ''}`}
        onClick={onOpenTask ? () => onOpenTask(task) : undefined}
        style={{
          gridTemplateColumns: `${largeurLibelles}px 1fr`,
          cursor: onOpenTask ? 'pointer' : 'default',
        }}
      >
        {compact ? (
          <div className="gantt-label-name" title={task.name}>{taskCode(task.id)}</div>
        ) : (
          <div>
            <div className="gantt-label-name">{task.name}</div>
            <div className="gantt-label-owner">
              {userDisplayName(users.find((u) => u.id === task.assigned_to))}
            </div>
          </div>
        )}
        <div className="gantt-track">
          {startDate && endDate ? (
            (() => {
              const start = dayOffset(startDate);
              const end = dayOffset(endDate);
              const left = pct(start);
              const width = Math.max(1.5, pct(Math.max(1, end - start)));
              // Sur une barre très courte, le pourcentage sortait tronqué
              // (« 10 » au lieu de « 100% »). On le pose alors juste à
              // droite de la barre plutôt que dedans.
              const etroite = width < 6;
              return (
                <>
                  <div
                    className="gantt-bar"
                    style={{
                      left: `${left}%`,
                      width: `${width}%`,
                      background: BAR_COLOR[task.status] || '#8CA0B3',
                    }}
                  >
                    {etroite ? '' : `${avancementTache(task)}%`}
                  </div>
                  {etroite && (
                    <div className="gantt-bar-etiquette" style={{ left: `${left + width}%` }}>
                      {avancementTache(task)}%
                    </div>
                  )}
                </>
              );
            })()
          ) : (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic', paddingLeft: 4 }}>
              Date manquante ou invalide — cliquez pour la corriger
            </div>
          )}
        </div>
      </div>
    );
  };

  // --- Rendu d'une ligne de groupe ------------------------------------------
  const ligneGroupe = (groupe) => {
    const { cle, jalon, taches } = groupe;
    const deplie = deplies.has(cle);

    const debuts = taches
      .flatMap((t) => [toValidDate(t.start_date), toValidDate(t.due_date)])
      .filter(Boolean);
    const debut = debuts.length ? new Date(Math.min(...debuts)) : toValidDate(jalon?.due_date);
    // La barre d'un jalon court jusqu'à la date du jalon, même si toutes ses
    // tâches finissent avant : c'est la date qui engage, pas la dernière tâche.
    const finsCandidates = [
      ...(debuts.length ? [new Date(Math.max(...debuts))] : []),
      ...(jalon ? [toValidDate(jalon.due_date)].filter(Boolean) : []),
    ];
    const fin = finsCandidates.length ? new Date(Math.max(...finsCandidates)) : null;

    const avancement = taches.length
      ? Math.round(taches.reduce((s, t) => s + avancementTache(t), 0) / taches.length)
      : jalon && jalon.status === 'Done'
      ? 100
      : 0;

    const termine = taches.filter((t) => t.status === 'Done').length;
    const dateJalon = jalon ? toValidDate(jalon.due_date) : null;
    const atteint =
      jalon &&
      (jalon.status === 'Done' || (taches.length > 0 && termine === taches.length));

    // « Hors jalon » reste toujours en bas : ce n'est pas un jalon, il n'a pas
    // de place à choisir dans la séquence.
    const glissable = !!jalon && !!onReorderJalons && !compact && groupesJalons.length > 1;
    const titre = jalon ? jalon.name : 'Hors jalon';
    const modifiable = !!jalon && !!onJalonChanged && !compact;

    // Déplacement du losange sur l'axe : la date suit la souris, jour par
    // jour, et s'enregistre au relâchement.
    const commencerDeplacement = (e, j) => {
      e.preventDefault();
      e.stopPropagation();
      const piste = e.currentTarget.parentElement;
      const rect = piste.getBoundingClientRect();
      const versIso = (clientX) => {
        const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
        const jours = Math.round(ratio * totalDays);
        const d = new Date(rangeStart.getTime() + jours * 86400000);
        return { iso: isoLocal(d), left: pct(jours) };
      };
      let dernier = { iso: String(j.due_date).slice(0, 10), left: pct(dayOffset(toValidDate(j.due_date))) };
      let bouge = false;
      setGlisseDate({ id: j.id, ...dernier });
      const surMouvement = (ev) => {
        bouge = true;
        dernier = versIso(ev.clientX);
        setGlisseDate({ id: j.id, ...dernier });
      };
      const fin = () => {
        document.removeEventListener('pointermove', surMouvement);
        document.removeEventListener('pointerup', fin);
        setGlisseDate(null);
        if (!bouge) { setJalonEdite(jalonEdite === j.id ? null : j.id); return; }
        if (dernier.iso === String(j.due_date).slice(0, 10)) return;
        api
          .put(`/tasks/${j.id}`, { due_date: dernier.iso, start_date: dernier.iso })
          .then(() => {
            setInfoDeplacement(`« ${j.name} » déplacé au ${formatDate(dernier.iso)}.`);
            window.setTimeout(() => setInfoDeplacement(''), 5000);
            onJalonChanged();
          })
          .catch(() => setInfoDeplacement('Le déplacement du jalon a échoué.'));
      };
      document.addEventListener('pointermove', surMouvement);
      document.addEventListener('pointerup', fin);
    };

    const resume = taches.length
      ? `${termine}/${taches.length} tâches · ${avancement}%`
      : 'aucune tâche rattachée';

    return (
      <React.Fragment key={cle}>
        <div
          className={
            `gantt-row gantt-row-groupe${deplie ? ' gantt-row-groupe-ouvert' : ''}` +
            (glissable ? ' gantt-row-glissable' : '') +
            (glisse === jalon?.id ? ' gantt-row-glisse' : '') +
            (cible === jalon?.id && glisse !== jalon?.id ? ' gantt-row-cible' : '')
          }
          draggable={glissable}
          onDragStart={(e) => {
            if (!glissable) return;
            aGlisse.current = true;
            prendre(jalon.id);
            // Firefox n'entame pas le glissement sans données transportées.
            e.dataTransfer.effectAllowed = 'move';
            try { e.dataTransfer.setData('text/plain', String(jalon.id)); } catch (err) { /* ignoré */ }
          }}
          onDragOver={(e) => {
            if (!glissable || glisseRef.current === null) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            if (cible !== jalon.id) setCible(jalon.id);
          }}
          onDrop={(e) => {
            if (!glissable || glisseRef.current === null) return;
            e.preventDefault();
            deposerJalon(jalon.id);
          }}
          onDragEnd={relacher}
          onClick={() => {
            // Un dépôt se termine parfois par un clic : on l'absorbe une fois,
            // sinon le groupe qu'on vient de déplacer se replierait tout seul.
            if (aGlisse.current) { aGlisse.current = false; return; }
            basculer(cle);
          }}
          style={{ gridTemplateColumns: `${largeurLibelles}px 1fr`, cursor: 'pointer' }}
        >
          {compact ? (
            // Sur le rapport imprimé il n'y a que trois ou quatre groupes :
            // autant écrire leur nom en clair, la colonne étroite suffit.
            <div className="gantt-label-groupe-compact" title={titre}>
              {jalon ? '◆ ' : ''}{titre}
              <span className="gantt-label-owner">{taches.length} tâches</span>
            </div>
          ) : (
            <div>
              <div className="gantt-label-name">
                <span className="gantt-chevron">{deplie ? '▾' : '▸'}</span>
                {jalon && <span className="gantt-jalon-puce">◆</span>}
                {titre}
                {modifiable && (
                  <button
                    type="button"
                    className="gantt-jalon-modifier"
                    title="Modifier le nom ou la date du jalon"
                    aria-label={`Modifier le jalon ${jalon.name}`}
                    onClick={(e) => { e.stopPropagation(); setJalonEdite(jalonEdite === jalon.id ? null : jalon.id); }}
                  >
                    ✎
                  </button>
                )}
              </div>
              <div className="gantt-label-owner">{resume}</div>

            </div>
          )}
          <div className="gantt-track">
            {debut && fin ? (
              (() => {
                const gauche = pct(dayOffset(debut));
                const largeur = Math.max(1.5, pct(Math.max(1, dayOffset(fin) - dayOffset(debut))));
                return (
                  <div
                    className="gantt-bar gantt-bar-groupe"
                    style={{ left: `${gauche}%`, width: `${largeur}%`, background: couleurGroupe(taches) }}
                  >
                    {largeur < 8 ? '' : `${avancement}%`}
                  </div>
                );
              })()
            ) : null}
            {dateJalon && (
              <div
                className={`gantt-jalon${atteint ? ' gantt-jalon-atteint' : ''}${glisseDate?.id === jalon.id ? ' gantt-jalon-en-deplacement' : ''}${modifiable ? ' gantt-jalon-modifiable' : ''}`}
                style={{ left: `${glisseDate?.id === jalon.id ? glisseDate.left : pct(dayOffset(dateJalon))}%` }}
                title={modifiable ? `${jalon.name} — ${formatDate(jalon.due_date)}. Glissez pour changer la date, cliquez pour modifier.` : jalon.name}
                onClick={(e) => { e.stopPropagation(); }}
                onPointerDown={modifiable ? (e) => commencerDeplacement(e, jalon) : undefined}
              >
                <span className="gantt-jalon-losange" />
                {glisseDate?.id === jalon.id && (
                  <span
                    className={`gantt-jalon-etiquette${glisseDate.left > 82 ? ' gantt-jalon-etiquette-gauche' : ''}`}
                  >
                    {formatDate(glisseDate.iso)}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {jalonEdite === jalon?.id && (
          <JalonEditeur
            jalon={jalon}
            onClose={() => setJalonEdite(null)}
            onSaved={() => onJalonChanged && onJalonChanged()}
          />
        )}

        {deplie && taches.map((t) => ligneTache(t, true))}
        {deplie && taches.length === 0 && (
          <div className="gantt-groupe-vide">
            Aucune tâche rattachée à ce jalon — utilisez « Rattacher des tâches » dans l’onglet Jalons.
          </div>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className="gantt-container">
      {infoDeplacement && (
        <div className="success-message" style={{ marginBottom: 10 }} onClick={() => setInfoDeplacement('')}>{infoDeplacement}</div>
      )}
      {groupable && !compact && (
        <div className="gantt-barre-outils">
          <span className="gantt-barre-info">
            {groupes.length} groupe{groupes.length > 1 ? 's' : ''} · {travail.length} tâches
            {groupesJalons.length > 1 && onReorderJalons && (
              <span className="gantt-barre-astuce"> · glissez une ligne de jalon pour changer l’ordre, glissez son losange ◆ pour changer sa date</span>
            )}
          </span>
          <button
            className="btn btn-sm"
            onClick={() =>
              setDeplies(toutDeplie ? new Set() : new Set(groupes.map((g) => g.cle)))
            }
          >
            {toutDeplie ? 'Tout replier' : 'Tout déplier'}
          </button>
        </div>
      )}

      <div className="gantt-row" style={{ gridTemplateColumns: `${largeurLibelles}px 1fr` }}>
        <div />
        <div style={{ position: 'relative', height: 34 }}>
          {months.map((m, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                left: `${m.left}%`,
                width: `${m.width}%`,
                fontSize: 10.5,
                fontWeight: 600,
                color: 'var(--text-muted)',
                borderLeft: '1px solid var(--border)',
                paddingLeft: 4,
              }}
            >
              {m.label} {m.year}
            </div>
          ))}
          {weekTicks.map((w, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                left: `${w.left}%`,
                top: 18,
                fontSize: 9.5,
                color: 'var(--text-muted)',
                borderLeft: '1px solid var(--border)',
                paddingLeft: 3,
                height: 12,
              }}
            >
              {w.label}
            </div>
          ))}
        </div>
      </div>

      <div style={{ position: 'relative' }}>
        {/* Le repère « Aujourd'hui » doit se mesurer sur la même largeur que
            les barres, c'est-à-dire la piste seule. Positionné directement sur
            le conteneur, il se calculait sur la largeur totale — colonne des
            libellés comprise — et se retrouvait décalé de plusieurs jours vers
            la gauche. D'où ce calque qui démarre après les libellés. */}
        {todayInRange && (
          <div
            style={{
              position: 'absolute',
              left: largeurLibelles,
              right: 0,
              top: 0,
              bottom: 0,
              pointerEvents: 'none',
              zIndex: 2,
            }}
          >
            <div
              style={{
                position: 'absolute',
                left: `${todayLeft}%`,
                top: 0,
                bottom: 0,
                width: 2,
                background: 'var(--danger)',
              }}
            >
              {/* Masqué à l'impression : l'étiquette chevauchait le nom du
                  mois, et la légende explique déjà le trait rouge. */}
              <div
                className="gantt-repere-aujourdhui"
                style={{
                  position: 'absolute',
                  top: -32,
                  left: -4,
                  fontSize: 9.5,
                  fontWeight: 600,
                  color: 'var(--danger)',
                  whiteSpace: 'nowrap',
                }}
              >
                Aujourd'hui
              </div>
            </div>
          </div>
        )}

        {groupes.map((g) => (g.plat ? g.taches.map((t) => ligneTache(t, false)) : ligneGroupe(g)))}
      </div>

      <div style={{ display: 'flex', gap: 20, marginTop: 16, fontSize: 11.5, color: 'var(--text-secondary)', flexWrap: 'wrap' }}>
        {groupable && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span className="gantt-jalon-losange" style={{ position: 'static', marginLeft: 0 }} />
            Jalon
          </div>
        )}
        {Object.entries({ 'À faire': BAR_COLOR.Todo, 'En cours': BAR_COLOR.In_Progress, Revue: BAR_COLOR.In_Review, Terminé: BAR_COLOR.Done, Bloqué: BAR_COLOR.Blocked }).map(([label, color]) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: color, display: 'inline-block' }} />
            {label}
          </div>
        ))}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 10, height: 2, background: 'var(--danger)', display: 'inline-block' }} />
          Aujourd'hui
        </div>
      </div>
    </div>
  );
}

export default Gantt;
